import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../../services/api';

interface DocumentUploadZoneProps {
  requestId: number;
  documentType?: string;
  rejectionMessage?: string;
  onUploadSuccess?: () => void;
  onUploadError?: (error: string) => void;
  compact?: boolean;
}

const DocumentUploadZone: React.FC<DocumentUploadZoneProps> = ({
  requestId,
  documentType = 'additional_document',
  rejectionMessage,
  onUploadSuccess,
  onUploadError,
  compact = false
}) => {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadDocument = async (file: File) => {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('request_id', requestId.toString());
    formData.append('document_type', documentType);

    setUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      const token = localStorage.getItem('authToken');

      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          setUploadProgress(Math.round(percentComplete));
        }
      });

      // Handle completion
      await new Promise((resolve, reject) => {
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(xhr.response);
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Network error during upload'));
        });

        xhr.open('POST', `${API_BASE_URL}/requests/upload.php`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.send(formData);
      });

      const result = JSON.parse(xhr.response);

      if (result.success) {
        setUploadedFiles(prev => [...prev, file.name]);
        setUploadProgress(100);

        // Reset after short delay
        setTimeout(() => {
          setUploadProgress(0);
          setUploading(false);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }, 1500);

        if (onUploadSuccess) {
          onUploadSuccess();
        }
      } else {
        throw new Error(result.error || 'Upload failed');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload document';
      setError(errorMessage);
      setUploading(false);
      setUploadProgress(0);

      if (onUploadError) {
        onUploadError(errorMessage);
      }
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // Upload files one by one
    for (let i = 0; i < files.length; i++) {
      await uploadDocument(files[i]);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  if (compact) {
    return (
      <div className="flex items-center space-x-2 rtl:space-x-reverse">
        {rejectionMessage && (
          <div className="flex-1 text-sm text-yellow-800 bg-yellow-50 px-3 py-2 rounded border border-yellow-200">
            <p className="font-medium mb-1">{t('requests.documentUploadRequired')}</p>
            <p className="whitespace-pre-line">{rejectionMessage}</p>
          </div>
        )}

        <div className="flex items-center space-x-2 rtl:space-x-reverse">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".jpeg,.jpg,.png,.webp,.pdf"
            onChange={handleFileSelect}
            className="hidden"
            disabled={uploading}
          />

          <button
            onClick={handleBrowseClick}
            disabled={uploading}
            className="bg-blue-500 text-white px-4 py-2 rounded text-sm hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {uploading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>{uploadProgress}%</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <span>{t('requests.uploadDocument')}</span>
              </>
            )}
          </button>
        </div>

        {error && (
          <div className="text-sm text-red-600 mt-1">
            {error}
          </div>
        )}

        {uploadedFiles.length > 0 && (
          <div className="text-sm text-green-600 mt-1">
            ✓ {uploadedFiles.length} {t('requests.filesUploaded')}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Rejection Message */}
      {rejectionMessage && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-yellow-400 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-yellow-800 mb-1">
                {t('requests.documentRejected')}
              </h4>
              <p className="text-sm text-yellow-700 whitespace-pre-line">
                {rejectionMessage}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Upload Zone */}
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".jpeg,.jpg,.png,.webp,.pdf"
          onChange={handleFileSelect}
          className="hidden"
          disabled={uploading}
        />

        {uploading ? (
          <div className="space-y-3">
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="text-sm text-gray-600">{t('requests.uploading')} {uploadProgress}%</p>
            <div className="w-full bg-gray-200 rounded-full h-2 max-w-xs mx-auto">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          </div>
        ) : (
          <>
            <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <p className="text-sm text-gray-600 mb-2">
              {t('requests.dragDropFiles')}
            </p>
            <button
              onClick={handleBrowseClick}
              className="bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600 text-sm"
            >
              {t('requests.browseFiles')}
            </button>
            <p className="text-xs text-gray-500 mt-2">
              {t('requests.supportedFileTypes')}
            </p>
          </>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-600">
            <strong>{t('requests.uploadError')}:</strong> {error}
          </p>
        </div>
      )}

      {/* Success Display */}
      {uploadedFiles.length > 0 && !uploading && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <p className="text-sm text-green-800 font-medium mb-2">
            ✓ {t('requests.successfullyUploaded')} {uploadedFiles.length} {t('requests.files')}:
          </p>
          <ul className="text-sm text-green-700 space-y-1">
            {uploadedFiles.map((filename, index) => (
              <li key={index}>• {filename}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default DocumentUploadZone;
